/** Customer, user, and location fields from an intake row for Ops review. */

import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";

export type IntakePartyPerson = {
  name: string | null;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
};

export type IntakePartyAddress = {
  label: string;
  lines: string[];
};

export type IntakeReviewUser = {
  label: string;
  name: string | null;
  email: string | null;
  detail: string | null;
};

export type IntakeReviewParties = {
  customer: IntakePartyPerson | null;
  addresses: IntakePartyAddress[];
  users: IntakeReviewUser[];
  sellerLocationId: string | null;
};

function asRec(v: unknown): Record<string, unknown> | null {
  if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
  return null;
}

function str(v: unknown): string {
  if (typeof v === "string" && v.trim()) return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return "";
}

function orderRoot(payload: Record<string, unknown>): Record<string, unknown> {
  return asRec(payload.order) ?? payload;
}

function personName(rec: Record<string, unknown> | null): string {
  if (!rec) return "";
  return (
    str(rec.name) ||
    [str(rec.first_name) || str(rec.firstName), str(rec.last_name) || str(rec.lastName)]
      .filter(Boolean)
      .join(" ")
  );
}

function emailOf(rec: Record<string, unknown> | null): string {
  return rec ? str(rec.email) : "";
}

function phoneOf(rec: Record<string, unknown> | null): string {
  if (!rec) return "";
  return str(rec.phone) || str(rec.phoneNumber) || str(rec.mobile) || str(rec.cell);
}

function personFrom(rec: Record<string, unknown> | null): IntakePartyPerson | null {
  if (!rec) return null;
  const name = personName(rec);
  const email = emailOf(rec);
  const phone = phoneOf(rec);
  const secondaryPhone = str(rec.secondaryPhone) || str(rec.secondary_phone);
  if (!name && !email && !phone && !secondaryPhone) return null;
  return {
    name: name || null,
    email: email || null,
    phone: phone || null,
    secondaryPhone: secondaryPhone || null,
  };
}

function addressLines(rec: Record<string, unknown> | null): string[] {
  if (!rec) return [];
  const street = [str(rec.address) || str(rec.address1) || str(rec.street), str(rec.address2)]
    .filter(Boolean)
    .join(", ");
  const cityLine = [
    str(rec.city),
    [str(rec.state), str(rec.zip) || str(rec.postalCode)].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityLine].filter(Boolean);
}

function pushUser(
  users: IntakeReviewUser[],
  label: string,
  rec: Record<string, unknown> | null,
  detail: string | null,
): void {
  if (!rec) return;
  const name = personName(rec);
  const email = emailOf(rec);
  if (!name && !email && !detail) return;
  users.push({ label, name: name || null, email: email || null, detail });
}

export function extractIntakeReviewParties(row: OpsOrderIntakeRow): IntakeReviewParties {
  const raw = row.raw_payload ?? {};
  const order = orderRoot(raw);
  const customerRec =
    asRec(raw.customer_contact) ??
    asRec(order.customer) ??
    asRec(raw.customer) ??
    asRec(order.buyer) ??
    asRec(raw.buyer);

  let customer = personFrom(customerRec);
  const optName = str(row.proposed_options?.customer_name);
  if (!customer && optName) {
    customer = { name: optName, email: null, phone: null, secondaryPhone: null };
  } else if (customer && !customer.name && optName) {
    customer = { ...customer, name: optName };
  }

  const addresses: IntakePartyAddress[] = [];
  const shipping = addressLines(
    asRec(order.shippingAddress) ?? asRec(raw.shippingAddress) ?? asRec(raw.shipping_address),
  );
  const contactAddress = customerRec ? str(customerRec.address) : "";
  if (shipping.length) {
    addresses.push({ label: "Shipping", lines: shipping });
  } else if (contactAddress) {
    addresses.push({ label: "Address", lines: [contactAddress] });
  } else {
    const fallback = [str(row.proposed_options?.ship_state), str(row.proposed_options?.ship_zip)]
      .filter(Boolean)
      .join(" ");
    if (fallback) addresses.push({ label: "Shipping", lines: [fallback] });
  }
  const billing = addressLines(asRec(order.billingAddress) ?? asRec(raw.billingAddress));
  if (billing.length) addresses.push({ label: "Billing", lines: billing });

  const users: IntakeReviewUser[] = [];
  const salesAccount = asRec(order.salesAccount) ?? asRec(raw.salesAccount);
  const salesAuth =
    salesAccount?.authenticated === true
      ? "Signed in"
      : salesAccount?.authenticated === false
        ? "Not signed in"
        : null;
  pushUser(users, "Sales account", salesAccount, salesAuth);

  const dealer = asRec(order.dealer) ?? asRec(raw.dealer);
  pushUser(users, "Dealer", dealer, str(dealer?.key) ? `Key ${str(dealer?.key)}` : null);

  const salesRep = asRec(order.salesRep) ?? asRec(raw.salesRep) ?? asRec(raw.sales_rep);
  pushUser(users, "Sales rep", salesRep, null);

  if (row.submitted_by_user_id) {
    users.push({
      label: "Submitted by",
      name: null,
      email: null,
      detail: row.submitted_by_user_id,
    });
  }

  return {
    customer,
    addresses,
    users,
    sellerLocationId: row.proposed_seller_location_id,
  };
}

export function intakeReviewPartiesHasContent(parties: IntakeReviewParties): boolean {
  return Boolean(
    parties.customer ||
      parties.addresses.length > 0 ||
      parties.users.length > 0 ||
      parties.sellerLocationId,
  );
}
